import {
  CSSProperties,
  FocusEventHandler,
  ReactNode,
  useEffect,
  useState,
} from "react"

import { CellProps, Col, TableProps } from "./types"
import { css, cx } from "@emotion/css"
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz"

import Button from "ui/buttons/Button"
import { CreateRow } from "./CreateRow"
import { DateRangeAgain } from "components/inputs/DateRange"
import Input from "ui/inputs/Input"
import MoreActions from "components/MoreActions"
import { StyledTable } from "./StyledTable"
import { TableLoading } from "components/tables/TableLoading"
import { TableToolbar } from "components/tables/TableToolbar"
import get from "lodash/get"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

interface RableProps<T> extends TableProps<T> {
  emptyState?: ReactNode
  pending?: boolean
  required?: Col<Partial<T>>[]
  hasToolbar?: boolean
  tableTitle?: string
  createBtnVariant?: "default" | "contained" | "outlined" | undefined
  clearError?: (rowId: string, name: string | keyof T) => void
  onOpen?: VoidFunction
  actionWidth?: CSSProperties["width"]
  btnClassName?: string
  createButtonLabel?: string
  disabledCondition?: (value: Partial<T>) => boolean
  actions?: (row: T) => Option[]
}

export function Rable<T extends Record<string, any>>(props: RableProps<T>) {
  const { t } = useI18n()
  const {
    data,
    columns,
    newRowColumns,
    //
    createRow,
    updateRow,
    //
    getError,
    clearError,
    label,
    //
    open,
    actionWidth,
    onClose,
    emptyState,
    pending,
    required,
    hasActions = true,
    hasToolbar = false,
    tableTitle,
    createBtnVariant = "contained",
    btnClassName,
    onOpen,
    createButtonLabel,
    disabledCondition,
    actions = () => [],
  } = props

  const [newRow, setNewRow] = useState<Partial<T>>({})

  const updateNewRow = (_: string, values: Partial<T>) => {
    setNewRow((s) => ({ ...s, ...values }))
  }

  const cols = hasActions
    ? ([
        ...columns,
        {
          id: `${label}.actions`,
          cell: (props) => <ActionCell {...props} actions={actions} />,
          align: "center",
          name: "actions",
          width: actionWidth ?? 180,
        },
      ] as Col<T>[])
    : columns

  const onCancel = () => {
    setNewRow({})
    onClose && onClose()
    // setErrors(undefined)
  }

  const onCreate = () => {
    createRow &&
      createRow(newRow).then(() => {
        setNewRow({})
        onClose && onClose()
      })
  }

  const actionColspan = !!newRowColumns?.length
    ? columns.length - newRowColumns.length + 1
    : undefined

  useEffect(() => setNewRow({}), [data])

  return (
    <>
      {hasToolbar ? (
        <div className={styles.toolbar}>
          <TableToolbar>
            <>
              <div className={styles.title}>{tableTitle}</div>
              {!!onOpen ? (
                <div className="right">
                  <Button
                    variant={createBtnVariant}
                    color="primary"
                    onClick={onOpen}
                    className={btnClassName}
                  >
                    {createButtonLabel}
                  </Button>
                </div>
              ) : null}
            </>
          </TableToolbar>
        </div>
      ) : null}
      {!!pending ? (
        <div>
          <TableLoading />
        </div>
      ) : !data?.length ? (
        open ? (
          <CreateRow
            required={required}
            row={newRow}
            columns={newRowColumns ?? []}
            onCancel={onCancel}
            onCreate={onCreate}
            onUpdate={updateNewRow}
            colspan={actionColspan}
            disabledCondition={
              !!disabledCondition ? () => disabledCondition(newRow) : undefined
            }
          />
        ) : (
          emptyState
        )
      ) : (
        <div className={styles.table}>
          <StyledTable style={{ borderBottom: "none" }}>
            <thead>
              <tr role="row">
                {cols.map((col) => (
                  <th
                    key={col.id}
                    role="columnheader"
                    className={cx(col.align)}
                    style={{ width: col.width }}
                  >
                    <span>{t(col.id)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {open ? (
                <CreateRow
                  required={required}
                  row={newRow}
                  columns={newRowColumns ?? []}
                  onCancel={onCancel}
                  onCreate={onCreate}
                  onUpdate={updateNewRow}
                  colspan={actionColspan}
                  disabledCondition={
                    !!disabledCondition
                      ? () => disabledCondition(newRow)
                      : undefined
                  }
                />
              ) : null}
              {data?.map((d, didx) => {
                return (
                  <tr key={d?.id} role="row">
                    {cols.map((col) => {
                      const {
                        id,
                        //
                        cell: Cell,
                        align = "left",
                        width,
                        //
                        getValue,
                        getOutput,
                        //
                        name,
                      } = col
                      return (
                        <td
                          role="cell"
                          key={`${didx}_${id}`}
                          className={cx(align)}
                          style={{
                            position: "relative",
                            width,
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                        >
                          <Cell
                            row={d}
                            rowId={d.id ?? ""}
                            name={name}
                            getValue={getValue}
                            getOutput={getOutput}
                            //
                            onCancel={onCancel}
                            onCreate={onCreate}
                            onUpdate={updateRow}
                            //
                            getError={getError}
                            clearError={clearError}
                          />
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </StyledTable>
        </div>
      )}
    </>
  )
}

export function NumberCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { row, rowId, name, getValue, getOutput, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""
  const hasError = Boolean(error)

  const initialValue = getValue ? getValue(row) : get(row, name, "")

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const hasValueDiff = initialValue.toString() !== value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)

    hasValueDiff && onUpdate(rowId, output)
  }

  const onChange: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    rowId === "NEW_ROW" && onUpdate("", output)
  }

  return (
    <div
      className={css`
        display: flex;
      `}
    >
      <Input
        name={`${row?.id}.${name as string}`}
        placeholder={t(`${name as string}.placeholder`)}
        defaultValue={initialValue}
        onChange={onChange}
        onBlur={onBlur}
        type="number"
      />
      {hasError ? <p className={"BodyBody-xsRedRegular"}>{t(error)}</p> : null}
    </div>
  )
}

export function TextCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { row, rowId, name, getValue, getOutput, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""
  const hasError = Boolean(error)

  const initialValue = getValue ? getValue(row) : get(row, name, "")

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const hasValueDiff = initialValue !== value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
  }
  const onChange: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    rowId === "NEW_ROW" && onUpdate("", output)
  }

  return (
    <div
      className={css`
        display: flex;
      `}
    >
      <Input
        name={`${row?.id}.${name as string}`}
        placeholder={t(`${name as string}.placeholder`)}
        defaultValue={initialValue}
        onChange={onChange}
        onBlur={onBlur}
        type="text"
      />
      {hasError ? <p className={"BodyBody-xsRedRegular"}>{t(error)}</p> : null}
    </div>
  )
}

export function DateRangeCell<T extends Record<string, any>>(
  props: CellProps<T>,
) {
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const { row, rowId, name, getValue, getOutput, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""
  const hasError = Boolean(error)

  const today = utcToZonedTime(new Date(), timeZone)
  const initialValue = getValue
    ? getValue(row)
    : get(row, name, { startDate: null, endDate: null })

  const start = initialValue?.startDate
  const end = initialValue?.endDate

  const onRangeChange = (s: Date | null, e: Date | null) => {
    if (!s || !e) return
    const value = {
      startDate: zonedTimeToUtc(s, timeZone).toISOString(),
      endDate: zonedTimeToUtc(e, timeZone).toISOString(),
    }
    const hasValueDiff = start !== value.startDate || end !== value.endDate
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
  }
  const { hasAuthority } = useAuthenticated()
  const allowed = hasAuthority([
    "AUTH_ACCESS_AGENCY_ADMIN",
    "AUTH_ACCESS_AGENCY_FINANCE",
  ])

  return (
    <div
      className={css`
        display: flex;
      `}
    >
      <DateRangeAgain
        start={start}
        end={end}
        minDate={today}
        onRangeChange={onRangeChange}
        className={styles.date}
        inputClassName={styles.dateInput}
        placeholder={t(`${name as string}.placeholder`)}
        disabled={allowed}
      />
      {hasError ? <p className={"BodyBody-xsRedRegular"}>{t(error)}</p> : null}
    </div>
  )
}

type Option = {
  label: string
  icon?: ReactNode
  onClick: (e?: any) => void
  hidden?: boolean
  disabled?: boolean
}

export function ActionCell<T extends Record<string, any>>(
  props: CellProps<T> & { actions: (row: T) => Option[] },
) {
  const { row } = props
  return (
    <div className={styles.actions}>
      <MoreActions options={props.actions(row)} />
    </div>
  )
}

const styles = {
  toolbar: css`
    padding: 0 0 var(--spacing-m);
  `,
  title: css`
    font-size: 14px;
    color: var(--black);
    font-weight: 700;
  `,
  table: css`
    overflow: auto;
    ::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 6px;
    }
    .MuiTable-root {
      border: solid 1px var(--light-grey);
      tr {
        :last-child {
          td {
            border-bottom: 0;
          }
        }
        th {
          color: var(--black);
          font-weight: 700;
        }
        th,
        td {
          border-bottom: solid 1px var(--light-grey);
          border-right: solid 1px var(--light-grey);

          :last-child {
            border-right: 0;
            text-align: center;
          }
        }
      }
    }
  `,
  date: css`
    .MuiOutlinedInput-marginDense {
      height: 40px;
    }
    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    width: 200px;
  `,
  dateInput: css`
    > input {
      font-size: 13px;
      outline: none;
      padding: 12px;
      margin-left: 1px;
    }
  `,
  buttonsContainer: css`
    text-align: center;
  `,
  actions: css`
    display: flex;
    justify-content: center;
  `,
}
